import { useState } from "react";
import PageHero from "@/components/PageHero";
import { api, formatApiErrorDetail, SCHOOL, whatsappShare, STATIC_MODE, submitToNetlify } from "@/lib/site";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Phone, Mail, MapPin, MessageCircle, Youtube, Instagram, Facebook, Send } from "lucide-react";

const NETLIFY_FORM = "contact";

export default function ContactPage() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill all required fields");
      return;
    }
    setSubmitting(true);
    try {
      
        await submitToNetlify(NETLIFY_FORM, form);
     
        
      
      toast.success("Message sent! The school has been notified.");

      // Also offer WhatsApp delivery for fastest response
      const wa = `Hello Royal English Medium High School,\n\n*Name:* ${form.name}\n*Email:* ${form.email}${form.phone ? `\n*Phone:* ${form.phone}` : ""}${form.subject ? `\n*Subject:* ${form.subject}` : ""}\n\n${form.message}`;
      window.open(whatsappShare(wa), "_blank");

      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Submission failed — try WhatsApp instead.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <PageHero eyebrow="CONTACT US" title="Get in Touch" subtitle="We'd love to hear from you. Visit our campus or reach out using the channels below." />

      <section className="py-20 px-6 bg-white">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-5 gap-10">
          {/* Contact info */}
          <div className="lg:col-span-2 space-y-5">
            <div className="bg-royal text-white rounded-3xl p-8">
              <h3 className="font-serif-display text-2xl font-bold">Reach Us Directly</h3>
              <div className="gold-divider" />
              <div className="space-y-5 mt-5">
                <a href={`tel:+${SCHOOL.whatsapp}`} className="flex items-start gap-4 hover:text-gold transition-colors" data-testid="contact-phone-1">
                  <div className="w-11 h-11 rounded-xl bg-white/10 flex items-center justify-center shrink-0"><Phone size={20} /></div>
                  <div>
                    <p className="text-xs uppercase tracking-widest text-gold">WhatsApp / Phone</p>
                    <p className="font-bold">{SCHOOL.whatsappDisplay}</p>
                  </div>
                </a>
                <a href={`tel:+${SCHOOL.phone}`} className="flex items-start gap-4 hover:text-gold transition-colors" data-testid="contact-phone-2">
                  <div className="w-11 h-11 rounded-xl bg-white/10 flex items-center justify-center shrink-0"><Phone size={20} /></div>
                  <div>
                    <p className="text-xs uppercase tracking-widest text-gold">Alternate Phone</p>
                    <p className="font-bold">{SCHOOL.phoneDisplay}</p>
                  </div>
                </a>
                <a href={`mailto:${SCHOOL.email}`} className="flex items-start gap-4 hover:text-gold transition-colors" data-testid="contact-email">
                  <div className="w-11 h-11 rounded-xl bg-white/10 flex items-center justify-center shrink-0"><Mail size={20} /></div>
                  <div>
                    <p className="text-xs uppercase tracking-widest text-gold">Email</p>
                    <p className="font-bold break-all">{SCHOOL.email}</p>
                  </div>
                </a>
                <a href={SCHOOL.mapUrl} target="_blank" rel="noreferrer" className="flex items-start gap-4 hover:text-gold transition-colors" data-testid="contact-address">
                  <div className="w-11 h-11 rounded-xl bg-white/10 flex items-center justify-center shrink-0"><MapPin size={20} /></div>
                  <div>
                    <p className="text-xs uppercase tracking-widest text-gold">Address</p>
                    <p className="font-bold">{SCHOOL.location}</p>
                  </div>
                </a>
              </div>
              <div className="mt-6 flex gap-3">
                <a href={`https://wa.me/${SCHOOL.whatsapp}`} target="_blank" rel="noreferrer" className="w-11 h-11 rounded-full bg-green-500 hover:bg-green-600 flex items-center justify-center" data-testid="contact-wa-btn"><MessageCircle size={18} /></a>
                <a href={SCHOOL.socials.youtube} target="_blank" rel="noreferrer" className="w-11 h-11 rounded-full bg-white/10 hover:bg-gold hover:text-royal flex items-center justify-center transition-colors"><Youtube size={18} /></a>
                <a href={SCHOOL.socials.instagram} target="_blank" rel="noreferrer" className="w-11 h-11 rounded-full bg-white/10 hover:bg-gold hover:text-royal flex items-center justify-center transition-colors"><Instagram size={18} /></a>
                <a href={SCHOOL.socials.facebook} target="_blank" rel="noreferrer" className="w-11 h-11 rounded-full bg-white/10 hover:bg-gold hover:text-royal flex items-center justify-center transition-colors"><Facebook size={18} /></a>
              </div>
            </div>

            <div className="rounded-3xl overflow-hidden shadow-lg border border-gray-100" data-testid="contact-map">
              <iframe
                title="Royal English School Location"
                src={SCHOOL.mapEmbed}
                width="100%"
                height="280"
                style={{ border: 0 }}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </div>

          {/* Form */}
          <div className="lg:col-span-3 bg-[#f7f7f7] rounded-3xl p-8 md:p-10 border border-gray-100">
            <h3 className="font-serif-display text-3xl font-bold text-royal">Send us a Message</h3>
            <div className="gold-divider" />
            <form
              onSubmit={submit}
              className="grid md:grid-cols-2 gap-5 mt-6"
              data-testid="contact-form"
              name={NETLIFY_FORM}
              method="POST"
              data-netlify="true"
              data-netlify-honeypot="bot-field"
            >
              <input type="hidden" name="form-name" value={NETLIFY_FORM} />
              <p className="hidden">
                <label>Don't fill this out: <input name="bot-field" /></label>
              </p>
              <div>
                <Label className="font-semibold">Your Name *</Label>
                <Input data-testid="contact-name" name="name" required value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5 h-11 bg-white" />
              </div>
              <div>
                <Label className="font-semibold">Email *</Label>
                <Input data-testid="contact-form-email" name="email" required type="email" value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5 h-11 bg-white" />
              </div>
              <div>
                <Label className="font-semibold">Phone</Label>
                <Input data-testid="contact-form-phone" name="phone" value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1.5 h-11 bg-white" />
              </div>
              <div>
                <Label className="font-semibold">Subject</Label>
                <Input data-testid="contact-subject" name="subject" value={form.subject}
                  onChange={(e) => setForm({ ...form, subject: e.target.value })} className="mt-1.5 h-11 bg-white" />
              </div>
              <div className="md:col-span-2">
                <Label className="font-semibold">Message *</Label>
                <Textarea data-testid="contact-message" name="message" required rows={5} value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })} className="mt-1.5 bg-white" />
              </div>
              <div className="md:col-span-2">
                <Button data-testid="contact-submit" type="submit" disabled={submitting}
                  className="w-full h-12 bg-royal hover:bg-gold hover:text-royal-dark text-white font-bold rounded-full inline-flex items-center justify-center gap-2 transition-all">
                  {submitting ? "Sending..." : <>Send Message <Send size={16} /></>}
                </Button>
              </div>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
}
